import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Menu, PhoneCall, ShieldCheck, X } from 'lucide-react';
import { trackEvent } from '../utils/tracking';

const LOGO_URL = '/greenlion_logo_cropped.svg';
const LOGO_MOBILE_URL = '/greenlion_new_logo.webp';

const PHONE_DISPLAY = '04221-2959978';
const PHONE_TEL = '042212959978';

const BRAND_GREEN = '#5EA74C';

const HeaderPowerdecker = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopServicesOpen, setDesktopServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  const [showMobileCtas, setShowMobileCtas] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollYRef = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();

  const servicePages = useMemo(
    () => [
      { name: 'Dachsanierung', path: '/service/dachsanierung', group: 'Dach' },
      { name: 'Dachdämmung', path: '/service/innendach-daemmung', group: 'Dach' },
      { name: 'Dach-Neubau', path: '/service/dach-neubau', group: 'Dach' },
      { name: 'Dachfenster', path: '/service/fensterausbau', group: 'Dach' },
      { name: 'Solaranlage', path: '/service/photovoltaik-montage', group: 'Photovoltaik' },
      { name: 'Dachsanierung + PV', path: '/dachsanierung-mit-photovoltaik', group: 'Photovoltaik' },
      { name: 'Wärmepumpe', path: '/waermepumpe', group: 'Wärmepumpe', badge: 'Neu' },
      { name: 'Photovoltaik + Wärmepumpe', path: '/photovoltaik-waermepumpe', group: 'Wärmepumpe' },
      { name: 'Förderservice & iSFP', path: '/service/foerderservice-isfp', group: 'Förderung' },
    ],
    []
  );
  const serviceGroups = useMemo(
    () => ['Dach', 'Photovoltaik', 'Wärmepumpe', 'Förderung'].map((g) => ({ name: g, items: servicePages.filter((p) => p.group === g) })),
    [servicePages]
  );

  const scrollToSection = (id) => {
    const doScroll = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const headerOffset = window.innerWidth >= 1280 ? 130 : 88;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    };

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(doScroll, 350);
    } else {
      doScroll();
    }

    setMobileOpen(false);
    setDesktopServicesOpen(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setDesktopServicesOpen(false);
        setMobileOpen(false);
        setMobileServicesOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Reset mobile services submenu when main menu closes
  useEffect(() => {
    if (!mobileOpen) {
      setMobileServicesOpen(false);
    }
  }, [mobileOpen]);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest('[data-services-dropdown]')) {
        setDesktopServicesOpen(false);
      }
    };

    if (desktopServicesOpen) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [desktopServicesOpen]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;

      // Sticky CTA bar appears after scrolling (not on form page)
      const onFormPage = window.location.pathname.startsWith('/projekt-anfrage');
      setShowMobileCtas(!onFormPage && y > 120);

      // Header behavior: scrolling down hides, scrolling up shows immediately
      const lastY = lastScrollYRef.current;
      if (y > lastY && y > 10) {
        setHeaderHidden(true);
      } else if (y < lastY) {
        setHeaderHidden(false);
      }
      lastScrollYRef.current = y;

      // If we're at the very top, keep it visible
      if (y <= 10) setHeaderHidden(false);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[9999] bg-white/80 backdrop-blur-md border-b border-black/10 shadow-sm transition-transform duration-300 ${
          headerHidden ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="h-20 lg:h-[120px] xl:h-[120px] flex items-center justify-between gap-4 overflow-hidden xl:overflow-visible">
            <div className="flex items-center gap-6">
              {/* Brand */}
              <Link
                to="/"
                className="flex items-center gap-3 min-w-0"
                aria-label="Green Lion Energy"
                onClick={() => {
                  setMobileOpen(false);
                  setDesktopServicesOpen(false);
                }}
              >
                <img
                  src={LOGO_URL}
                  alt="Green Lion Energy Logo"
                  className="hidden lg:block h-[110px] xl:h-[110px] w-auto object-contain"
                  style={{ paddingTop: 0, paddingBottom: 0 }}
                  width={400} height={110}
                  fetchPriority="high"
                />
                <img src={LOGO_MOBILE_URL} alt="Green Lion Energy Logo" className="lg:hidden max-h-14 max-w-[218px] object-contain" width={218} height={56} loading="eager" decoding="async" />
              </Link>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-8 text-[15px] font-semibold text-gray-900">
                <div className="relative" data-services-dropdown>
                  <button
                    type="button"
                    onClick={() => setDesktopServicesOpen((v) => !v)}
                    onMouseEnter={() => setDesktopServicesOpen(true)}
                    className="inline-flex items-center gap-2 hover:text-gray-700"
                    aria-haspopup="true"
                    aria-expanded={desktopServicesOpen}
                  >
                    Leistungen
                    <ChevronDown className={`w-4 h-4 transition-transform ${desktopServicesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {desktopServicesOpen && (
                    <div
                      className="absolute top-full left-0 mt-3 w-[560px] rounded-3xl border border-black/10 bg-white/90 backdrop-blur-md shadow-2xl"
                      onMouseLeave={() => setDesktopServicesOpen(false)}
                      role="menu"
                      data-testid="header-services-menu"
                    >
                      <div className="p-3 grid grid-cols-2 gap-2">
                        {serviceGroups.map((g) => (
                          <div key={g.name} className="p-1" data-testid={`header-group-${g.name.toLowerCase().replace('ä', 'ae')}`}>
                            <p className="px-3 pt-2 pb-1 text-[11px] font-extrabold uppercase tracking-widest text-gray-400">{g.name}</p>
                            {g.items.map((p) => (
                              <Link
                                key={p.path}
                                to={p.path}
                                onClick={() => setDesktopServicesOpen(false)}
                                className="flex items-center justify-between rounded-2xl px-3 py-2.5 hover:bg-black/5 text-gray-900 text-[15px]"
                                role="menuitem"
                              >
                                <span>{p.name}</span>
                                {p.badge && <span className="ml-2 rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-black uppercase text-white">{p.badge}</span>}
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Link to="/ueber-uns" className="hover:text-gray-700">
                  Über uns
                </Link>
                <Link to="/referenzen" className="hover:text-gray-700">
                  Referenzen
                </Link>
              </nav>
            </div>

            {/* Desktop Right */}
            <div className="hidden lg:flex items-center gap-6">
              <div className="flex flex-col leading-tight">
                <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center min-h-[24px] py-0.5 text-[15px] font-semibold text-gray-900 hover:text-gray-700">
                  {PHONE_DISPLAY}
                </a>
                <a href="mailto:anfrage@greenlionenergy.de" className="inline-flex items-center min-h-[24px] py-0.5 text-[15px] font-semibold text-gray-900 hover:text-gray-700">
                  anfrage@greenlionenergy.de
                </a>
              </div>

              <Link
                to="/projekt-anfrage"
                className="gle-cta inline-flex items-center justify-center rounded-2xl px-6 py-3 font-bold text-white transition-opacity"
                data-testid="header-cta"
              >
                Kostenlose Ersteinschätzung
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>

            {/* Mobile controls - nur 3 Striche ohne Umrandung */}
            <div className="lg:hidden flex items-center">
              <button
                type="button"
                onClick={() => setMobileOpen((v) => !v)}
                className="inline-flex items-center justify-center w-10 h-10 text-gray-700"
                aria-label={mobileOpen ? 'Menü schließen' : 'Menü öffnen'}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/45 z-[9998] lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />

          <div className="fixed inset-0 z-[9999] lg:hidden overflow-hidden">
            <div className="absolute inset-0 bg-white/95 backdrop-blur-md" />

            <div className="relative h-full flex flex-col">
              {/* Mobile Menu Header - ohne Logo */}
              <div className="h-12 px-4 flex items-center justify-end border-b border-black/10 shrink-0">
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-black/10 bg-white/80"
                  aria-label="Menü schließen"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Menu Content - Alle Leistungen direkt sichtbar */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <nav className="flex flex-col gap-1">
                  {/* Startseite Link */}
                  <Link
                    to="/"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 py-2.5 px-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-[14px] font-medium text-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Startseite
                  </Link>

                  {/* Leistungen - gruppiert, alle direkt sichtbar */}
                  {serviceGroups.map((g) => (
                    <React.Fragment key={g.name}>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-3 mb-1 px-3">
                        {g.name}
                      </p>
                      {g.items.map((p) => (
                        <Link
                          key={p.name}
                          to={p.path}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-gray-100 text-[14px] font-medium text-gray-800 transition-colors"
                          data-testid={`mobile-nav-${p.path.replace(/\//g, '-').replace(/^-/, '')}`}
                        >
                          {p.name}
                          {p.badge && <span className="rounded-full bg-green-600 px-2 py-0.5 text-[10px] font-black uppercase text-white">{p.badge}</span>}
                        </Link>
                      ))}
                    </React.Fragment>
                  ))}

                  {/* Über uns */}
                  <Link
                    to="/ueber-uns"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center py-2.5 px-3 mt-2 rounded-xl hover:bg-gray-100 text-[14px] font-semibold text-gray-900 transition-colors"
                  >
                    Über uns
                  </Link>
                  <Link
                    to="/referenzen"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center py-2.5 px-3 rounded-xl hover:bg-gray-100 text-[14px] font-semibold text-gray-900 transition-colors"
                  >
                    Referenzen
                  </Link>
                </nav>

                {/* Contact & CTA Section */}
                <div className="mt-4 pt-4 border-t border-black/10">
                  <div className="grid grid-cols-2 gap-2">
                    <a
                      href={`tel:${PHONE_TEL}`}
                      className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-semibold text-sm px-3 py-2.5 rounded-xl border border-black/10"
                    >
                      <PhoneCall className="w-4 h-4" />
                      Anrufen
                    </a>
                    <Link
                      to="/projekt-anfrage"
                      onClick={() => setMobileOpen(false)}
                      className="gle-cta inline-flex items-center justify-center text-white font-semibold text-sm px-3 py-2.5 rounded-xl"
                    >
                      Anfragen
                      <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Mobile Sticky CTA Bar — Anrufen · WhatsApp · Anfrage */}
      <div
        className={`md:hidden fixed left-0 right-0 bottom-0 z-[55] transition-transform duration-300 ${
          showMobileCtas ? 'translate-y-0' : 'translate-y-full'
        }`}
        aria-hidden={!showMobileCtas}
        inert={!showMobileCtas}
        data-testid="mobile-cta-bar"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="bg-white/95 backdrop-blur-md border-t border-black/10 shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.08)]">
          <div className="mx-auto max-w-screen-xl px-3 py-2.5">
            <div className="grid grid-cols-3 gap-2">
              {/* Anrufen */}
              <a
                href={`tel:${PHONE_TEL}`}
                onClick={() => trackEvent('phone_click', {
                  source: 'mobile_cta_bar',
                  meta: { source: 'mobile_cta_bar', device: 'mobile' },
                })}
                className="inline-flex flex-col items-center justify-center gap-1 bg-white text-gray-900 font-semibold text-xs px-2 py-2.5 rounded-2xl border border-slate-200 active:bg-slate-50"
                data-testid="mobile-cta-call"
                aria-label="Anrufen"
              >
                <PhoneCall className="w-5 h-5 text-emerald-700" />
                <span>Anrufen</span>
              </a>

              {/* WhatsApp */}
              <a
                href="https://wa.me/4942212959978"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', {
                  source: 'mobile_cta_bar',
                  meta: { source: 'mobile_cta_bar', device: 'mobile' },
                })}
                className="inline-flex flex-col items-center justify-center gap-1 bg-white text-gray-900 font-semibold text-xs px-2 py-2.5 rounded-2xl border border-slate-200 active:bg-emerald-50"
                data-testid="mobile-cta-whatsapp"
                aria-label="WhatsApp"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  className="w-5 h-5"
                  fill="#25D366"
                  aria-hidden="true"
                >
                  <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.413c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.521.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
                </svg>
                <span>WhatsApp</span>
              </a>

              {/* Anfrage */}
              <Link
                to="/projekt-anfrage"
                onClick={() => trackEvent('consultation_click', {
                  source: 'mobile_cta_bar',
                  meta: { source: 'mobile_cta_bar', device: 'mobile', cta: 'anfrage' },
                })}
                className="gle-cta inline-flex flex-col items-center justify-center gap-1 text-white font-semibold text-xs px-2 py-2.5 rounded-2xl"
                data-testid="mobile-cta-anfrage"
                aria-label="Anfrage senden"
              >
                <ArrowRight className="w-5 h-5" />
                <span>Anfrage</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HeaderPowerdecker;
